/**
 * Service pour gérer les interactions avec l'API FedEx via notre backend
 * Version simplifiée qui appelle directement les endpoints du backend
 */

import axios from 'axios';
import { supabase } from '../supabaseClient';
import { apiEndpoint } from '../config/api';

/**
 * Récupère la liste des expéditions FedEx
 * @param {Object} params Paramètres de pagination
 * @returns {Promise<Object>} Liste des expéditions
 */
export const getFedexShipments = async (params = {}) => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Utilisateur non authentifié');

    // Appeler notre backend pour récupérer les expéditions
    const response = await axios.get(apiEndpoint('/api/fedex/shipments'), {
      headers: {
        Authorization: `Bearer ${session.access_token}`
      },
      params
    });
    
    return response.data;
  } catch (error) {
    console.error('Erreur lors de la récupération des expéditions FedEx:', error);
    throw error;
  }
};

/**
 * Récupère les détails d'une expédition FedEx par son numéro de suivi
 * @param {string} trackingNumber Numéro de suivi de l'expédition
 * @returns {Promise<Object>} Détails de l'expédition
 */
export const getFedexShipmentByTrackingNumber = async (trackingNumber) => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Utilisateur non authentifié');

    // Appeler notre backend pour récupérer les détails de l'expédition
    const response = await axios.get(apiEndpoint(`/api/fedex/shipments/${trackingNumber}`), {
      headers: {
        Authorization: `Bearer ${session.access_token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de la récupération de l'expédition FedEx ${trackingNumber}:`, error);
    throw error;
  }
};

/**
 * Crée une nouvelle expédition FedEx
 * @param {Object} shipmentData Données de l'expédition
 * @returns {Promise<Object>} Expédition créée
 */
export const createFedexShipment = async (shipmentData) => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Utilisateur non authentifié');

    // Appeler notre backend pour créer l'expédition
    const response = await axios.post(apiEndpoint('/api/fedex/shipments'), shipmentData, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data;
  } catch (error) {
    console.error('Erreur lors de la création de l\'expédition FedEx:', error);
    throw error;
  }
};

/**
 * Annule une expédition FedEx
 * @param {string} trackingNumber Numéro de suivi de l'expédition à annuler
 * @returns {Promise<Object>} Résultat de l'annulation
 */
export const cancelFedexShipment = async (trackingNumber) => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Utilisateur non authentifié');

    // Appeler notre backend pour annuler l'expédition
    const response = await axios.post(apiEndpoint('/api/fedex/shipments/cancel'),
      { trackingNumber },
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error(`Erreur lors de l'annulation de l'expédition FedEx ${trackingNumber}:`, error);
    throw error;
  }
};

/**
 * Télécharge l'étiquette d'une expédition FedEx
 * @param {string} labelUrl URL FedEx retournée lors de la création
 * @returns {Promise<Blob>} Étiquette au format PDF
 */
export const downloadFedexLabel = async (labelUrl) => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Utilisateur non authentifié');

    // Appeler notre backend pour télécharger l'étiquette
    const response = await axios.get(apiEndpoint('/api/fedex/label'), {
      headers: {
        Authorization: `Bearer ${session.access_token}`
      },
      params: { url: labelUrl },
      responseType: 'blob'
    });
    
    return response.data;
  } catch (error) {
    console.error("Erreur lors du téléchargement de l'étiquette FedEx:", error);
    throw error;
  }
};

/**
 * Suivi de plusieurs expéditions FedEx par leur numéro de suivi
 * @param {string[]} trackingNumbers Tableau de numéros de suivi
 * @returns {Promise<Object>} Résultats du suivi
 */
export const trackShipments = async (trackingNumbers) => {
  try {
    // Vérifier si l'utilisateur est authentifié
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Utilisateur non authentifié');

    // Appeler notre backend pour le suivi
    const response = await axios.post(apiEndpoint('/api/fedex/track'),
      { trackingNumbers },
      {
        headers: {
          Authorization: `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    return response.data;
  } catch (error) {
    console.error(`Erreur lors du suivi des expéditions FedEx:`, error);
    throw error;
  }
};

/**
 * Récupère la preuve de livraison (SPOD) pour une expédition donnée
 * @param {string} trackingNumber - Numéro de suivi
 * @returns {Promise<{documentFormat: string, filename: string, data: string}>}
 */
export const getProofOfDelivery = async (trackingNumber) => {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Utilisateur non authentifié');

    const response = await axios.post(apiEndpoint(`/api/fedex/shipments/${trackingNumber}/proof-of-delivery`), {}, {
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        'Content-Type': 'application/json'
      }
    });

    return response.data; // { documentFormat, filename, data: base64 }
  } catch (error) {
    // Normaliser l'erreur pour l'UI: récupérer message/status/details depuis le backend
    const status = error?.response?.status;
    const payload = error?.response?.data;
    const msg = payload?.message || error?.message || 'Erreur inconnue';
    const details = payload?.details;
    const err = new Error(msg);
    err.status = status;
    err.details = details;
    err.raw = payload;
    console.error(`Erreur lors de la récupération de la preuve de livraison FedEx ${trackingNumber}:`, { status, msg, details });
    throw err;
  }
};
